import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {string | null} */
  #pendingHighlightKey = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener('try-on:open-cart-dts', this.#handleOpenDts);
    this.addEventListener('click', this.#handleClick);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#highlightPendingLineItem);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#renderDtsPanel);
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#renderDtsPanel();
    this.#syncCartTabCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener('try-on:open-cart-dts', this.#handleOpenDts);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#highlightPendingLineItem);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#renderDtsPanel);
    this.removeEventListener(DialogOpenEvent.eventName, this.#onDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #onDialogOpen = () => {
    this.#syncCartTabCount();
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const explicitHighlightKey = event.detail?.data?.lineItemKey;
    const fallbackHighlightKey = Array.isArray(event.detail?.data?.lineItemKeys)
      ? event.detail.data.lineItemKeys[event.detail.data.lineItemKeys.length - 1]
      : null;

    this.#pendingHighlightKey = explicitHighlightKey || fallbackHighlightKey || null;

    if (this.hasAttribute('auto-open')) {
      this.#setTab('cart');
      this.showDialog();
    }

    const itemCount = event.detail.resource?.item_count;
    this.#announceCartCount(itemCount);
    if (itemCount !== undefined) this.#updateCartTabCount(itemCount);
  };

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const dataCount = event.detail?.data?.itemCount;
    const resourceCount = event.detail?.resource?.item_count;

    if (typeof dataCount === 'number') {
      this.#updateCartTabCount(dataCount);
    } else if (resourceCount !== undefined && resourceCount !== null) {
      this.#updateCartTabCount(resourceCount);
    } else {
      this.#syncCartTabCount();
    }

    /* Section morph can reset client-only DTS UI (tab count + list). */
    this.#scheduleDtsPanelRefresh();
  };

  #scheduleDtsPanelRefresh() {
    requestAnimationFrame(() => {
      this.#renderDtsPanel();
    });
  }

  #syncCartTabCount() {
    const countEl = this.querySelector('[ref="cartItemCount"]');
    if (countEl) {
      const count = parseInt(countEl.textContent || '0', 10);
      if (!Number.isNaN(count)) {
        this.#updateCartTabCount(count);
        return;
      }
    }

    const rows = this.querySelectorAll('.cart-items__table-row[data-key]');
    this.#updateCartTabCount(rows.length);
  }

  /**
   * @param {number} count
   */
  #updateCartTabCount(count) {
    const el = this.querySelector('[data-cart-tab-count]');
    if (!el) return;
    const safe = Math.max(0, Number(count) || 0);
    el.textContent = safe ? `(${safe})` : '';
    el.hidden = !safe;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    const tab = target.closest('[data-cart-tab]');
    if (tab instanceof HTMLElement && this.contains(tab)) {
      this.#setTab(tab.getAttribute('data-cart-tab') || 'cart');
      return;
    }

    const browseLink = target.closest('[data-dts-browse]');
    if (browseLink instanceof HTMLAnchorElement && this.contains(browseLink)) {
      this.close();
      return;
    }

    const bookBtn = target.closest('[data-dts-book]');
    if (bookBtn instanceof HTMLButtonElement && this.contains(bookBtn) && !bookBtn.disabled && !bookBtn.hidden) {
      this.close();
      window.TryOnDTS?.openBooking();
    }
  };

  #handleOpenDts = () => {
    this.#setTab('dts');
    this.showDialog();
  };

  /**
   * @param {string} tab
   */
  #setTab(tab) {
    const tabs = this.querySelectorAll('[data-cart-tab]');
    const panels = this.querySelectorAll('[data-cart-panel]');

    tabs.forEach((el) => {
      const active = el.getAttribute('data-cart-tab') === tab;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    panels.forEach((el) => {
      const active = el.getAttribute('data-cart-panel') === tab;
      el.classList.toggle('is-active', active);
      if (active) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    if (tab === 'dts') {
      this.#renderDtsPanel();
    }
  }

  #renderDtsPanel = () => {
    const panel = this.querySelector('[data-dts-panel]');
    if (panel && window.TryOnDTS) {
      window.TryOnDTS.renderCartPanel(panel);
    }
  };

  #highlightPendingLineItem = () => {
    if (isMobileBreakpoint()) {
      this.#pendingHighlightKey = null;
      return;
    }

    const highlightKey = this.#pendingHighlightKey;
    this.#pendingHighlightKey = null;

    if (!highlightKey) {
      return;
    }

    requestAnimationFrame(() => {
      const row = this.querySelector(`[data-key="${CSS.escape(highlightKey)}"]`);

      if (!(row instanceof HTMLElement) || !row.classList.contains('cart-items__table-row--full-width-variants')) {
        return;
      }

      row.classList.remove('cart-items__table-row--recently-updated');
      void row.offsetWidth;
      row.classList.add('cart-items__table-row--recently-updated');

      onAnimationEnd(
        row,
        () => {
          row.classList.remove('cart-items__table-row--recently-updated');
        },
        { subtree: false }
      );
    });
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.#setTab('cart');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
